import { useState, useMemo } from 'react'
import Card from 'shared/Cards/Card'
import TextField from 'shared/Forms/TextField'
import NumberInput from 'shared/Inputs/NumberInput'
import Textarea from 'shared/Inputs/Textarea'
import Grade from 'shared/Grade'
import Button from 'shared/Buttons/Button/Button'
import EditButton from 'shared/Buttons/EditButton'
import { useCheckpointCriteriaState } from 'screens/Admin/ProjectCheckpoints/hooks/useCheckpointCriteriaState'
import { InspectorCheckResult, InspectorCheckResultCriteriaResult } from '../../../../inspection.types'
import { IProjectCheckpointCriterion } from 'screens/Admin/ProjectCheckpoints/projectCheckpoints.types'
import { useRecoilValue } from 'recoil'
import authAtom from 'screens/Auth/auth.atom'
import inspectionApi from '../../../../inspection.api'

interface IProps {
  checkResult: InspectorCheckResult
  checkpointId: string
  setCheckResult: (next: InspectorCheckResult | ((prev: InspectorCheckResult) => InspectorCheckResult)) => void
}

const criteriaGradeMap = (criteriaResults: InspectorCheckResultCriteriaResult[]): Record<string, number> =>
  criteriaResults.reduce((acc, r) => ({ ...acc, [r.projectCheckpointCriteriaId]: r.grade }), {})

const averageGrade = (grades: number[]): number | null => {
  if (grades.length === 0) return null
  const sum = grades.reduce((a, b) => a + b, 0)
  return Math.round((sum / grades.length) * 10) / 10
}

const SubmissionGradesCard = (props: IProps) => {
  const { checkResult, checkpointId, setCheckResult } = props
  const { criteriaState } = useCheckpointCriteriaState(checkpointId)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [commentDraft, setCommentDraft] = useState(checkResult.comment)
  const [criteriaGradesDraft, setCriteriaGradesDraft] = useState<Record<string, number>>(() =>
    criteriaGradeMap(checkResult.criteriaResults)
  )

  const currentUser = useRecoilValue(authAtom).user

  const criteriaResultsMap = useMemo(() => criteriaGradeMap(checkResult.criteriaResults), [checkResult.criteriaResults])

  const displayGrade = useMemo(() => {
    if (editing) {
      const grades = criteriaState.map((c) => criteriaGradesDraft[c.id] ?? 0)
      return averageGrade(grades)
    }
    return checkResult.grade
  }, [editing, criteriaState, criteriaGradesDraft, checkResult.grade])

  const handleEdit = () => {
    setCommentDraft(checkResult.comment)
    setCriteriaGradesDraft(criteriaGradeMap(checkResult.criteriaResults))
    setEditing(true)
  }

  const handleCancel = () => {
    setCommentDraft(checkResult.comment)
    setCriteriaGradesDraft(criteriaGradeMap(checkResult.criteriaResults))
    setEditing(false)
  }

  const handleChange = (val: string, name: string) => {
    if (name === 'comment') setCommentDraft(val)
  }

  const handleCriterionGradeChange = (criterionId: string, value: number, criterion: IProjectCheckpointCriterion) => {
    if (value > criterion.maxBalls) return
    setCriteriaGradesDraft((prev) => ({ ...prev, [criterionId]: value }))
  }

  const handleSave = () => {
    const grades = criteriaState.map((c) => criteriaGradesDraft[c.id] ?? 0)
    const grade = averageGrade(grades)
    if (grade === null) return
    setSaving(true)
    inspectionApi
      .submitPassCheckResult(checkResult.projectId, checkResult.checkpointId, {
        teamId: checkResult.teamId,
        grade,
        comment: commentDraft,
        criteriaResults: criteriaState.map((c) => ({
          projectCheckpointCriteriaId: c.id,
          grade: criteriaGradesDraft[c.id] ?? 0,
        })),
      })
      .then((resp) => {
        setSaving(false)
        if (resp.status === 'success') {
          setCheckResult(resp.body)
          setEditing(false)
        }
      })
  }

  const criteriaByColumn = useMemo(() => {
    const mid = Math.ceil(criteriaState.length / 2)
    return [criteriaState.slice(0, mid), criteriaState.slice(mid)]
  }, [criteriaState])

  const renderCriterionRead = (criterion: IProjectCheckpointCriterion) => (
    <TextField key={criterion.id} header={`${criterion.title}`}>
      {`${criteriaResultsMap[criterion.id] ?? '—'} / ${criterion.maxBalls}`}
    </TextField>
  )

  const renderCriterionEdit = (criterion: IProjectCheckpointCriterion) => (
    <TextField
      key={criterion.id}
      header={`${criterion.title} (${criteriaGradesDraft[criterion.id] ?? 0}/${criterion.maxBalls})`}
    >
      <NumberInput
        name={criterion.id}
        value={criteriaGradesDraft[criterion.id] ?? 0}
        onChange={(val, name) => handleCriterionGradeChange(name, val, criterion)}
      />
    </TextField>
  )

  const renderContent = () => {
    if (editing) {
      return (
        <div className='flex-lines'>
          <div className='flex-space-between' style={{ marginBottom: '16px', width: '100%' }}>
            <h2 className='text--caption-l text-700'>Оценки</h2>
          </div>
          <Grade grade={displayGrade} />
          <TextField header='Комментарий эксперта'>
            <Textarea
              name='comment'
              value={commentDraft}
              onChange={(val, name) => handleChange(val, name)}
              required={false}
              height='80px'
            />
          </TextField>
          <div className='grid-form-2' style={{ marginTop: '16px' }}>
            <div className='flex-lines' style={{ gap: '12px' }}>
              {criteriaByColumn[0].map((c) => renderCriterionEdit(c))}
            </div>
            <div className='flex-lines' style={{ gap: '12px' }}>
              {criteriaByColumn[1].map((c) => renderCriterionEdit(c))}
            </div>
          </div>
          <div className='form-footer'>
            <div className='inline-flex-gap'>
              <Button
                theme='primary'
                style={{ width: 'fit-content' }}
                onClick={handleSave}
                loading={saving}
                disabled={saving}
              >
                Сохранить
              </Button>
              <Button theme='grey-outline' style={{ width: 'fit-content' }} onClick={handleCancel} disabled={saving}>
                Отмена
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className='flex-lines'>
        <div className='flex-space-between' style={{ marginBottom: '16px', width: '100%' }}>
          <h2 className='text--caption-l text-700'>Оценки</h2>
          {(checkResult.inspectorId === currentUser.id || currentUser.isAdmin) && <EditButton onClick={handleEdit} />}
        </div>
        <Grade grade={displayGrade} />
        <TextField header='Комментарий эксперта'>{checkResult.comment || '—'}</TextField>
        <div className='grid-form-2' style={{ marginTop: '16px' }}>
          <div className='flex-lines' style={{ gap: '12px' }}>
            {criteriaByColumn[0].map(renderCriterionRead)}
          </div>
          <div className='flex-lines' style={{ gap: '12px' }}>
            {criteriaByColumn[1].map(renderCriterionRead)}
          </div>
        </div>
      </div>
    )
  }

  return <Card type='normal'>{renderContent()}</Card>
}

export default SubmissionGradesCard
